/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type {
  AlternativeProduct,
  GetPastProductsResponse,
  PastProduct,
  PastProductOrder,
  PriceInfo,
  ProductAvailability,
} from "@farmlands/farmlandspro-api";

import { ActivePrice, Order, Product, StockAvailabilityType } from "../storage/types";

const toOrder = (order: PastProductOrder): Order => ({
  quantity: order.quantity ?? 0,
  unit: order.unit ?? "",
  createdAt: order.createdAt ?? "",
});

const toActivePrice = (price: PriceInfo): ActivePrice => ({
  productId: price.productId ?? 0,
  basePriceExcGst: price.basePriceExcGst ?? 0,
  discountedPriceExcGst: price.discountedPriceExcGst ?? 0,
  discountedPriceIncGst: price.discountedPriceIncGst ?? 0,
});

export const toActivePrices = (prices?: PriceInfo[]): ActivePrice[] =>
  prices?.map(toActivePrice) ?? [];

const toStockAvailability = (availability: ProductAvailability): StockAvailabilityType => ({
  productId: availability.productId ?? 0,
  inventLocationId: availability.inventLocationId ?? "",
  quantity: availability.quantity ?? 0,
  inventoryLevelLabel: availability.inventoryLevelLabel ?? "",
  inventoryLevelCode: availability.inventoryLevelCode ?? "",
  isBackOrderAllowed: availability.isBackOrderAllowed ?? false,
});

export const toStockAvailabilities = (
  availabilities?: ProductAvailability[]
): StockAvailabilityType[] => availabilities?.map(toStockAvailability) ?? [];

const toAlternativeProduct = (alt: AlternativeProduct): Product => ({
  productId: alt.productId ?? 0,
  masterProductId: alt.masterProductId ?? 0,
  itemNumber: alt.itemNumber ?? "",
  name: alt.name ?? "",
  description: "",
  category: "Miscellaneous",
  lifeCycleState: alt.lifeCycleState ?? null,
  basePriceExcGst: alt.basePriceExcGst ?? 0,
  basePriceIncGst: 0,
  discountedPriceExcGst: alt.discountedPriceExcGst ?? 0,
  discountedPriceIncGst: alt.discountedPriceIncGst ?? 0,
  orders: [],
  size: alt.size,
  colour: alt.colour,
  isPriceNegotiable: alt.isPriceNegotiable,
});

const toProduct = (x: PastProduct): Product => ({
  ...x,
  productId: x.productId ?? 0,
  masterProductId: x.masterProductId ?? 0,
  name: x.name ?? "",
  description: x.description ?? "",
  category: x.category ?? "Miscellaneous",
  itemNumber: x.itemNumber ?? x.productVariantNumber ?? "",
  lifeCycleState: x.lifeCycleState ?? null,
  basePriceExcGst: x.basePriceExcGst ?? 0,
  basePriceIncGst: x.nationalPrice ?? 0,
  discountedPriceExcGst: x.discountedPriceExcGst ?? 0,
  discountedPriceIncGst: x.discountedPriceIncGst ?? 0,
  orders: (x.orders ?? []).map(toOrder),
  alternativeProduct: x.alternativeProduct ? toAlternativeProduct(x.alternativeProduct) : undefined,
});

export const transformProducts = (data: GetPastProductsResponse): Product[] => {
  const products = (data.products ?? []).filter((a) => (a.orders?.length ?? 0) > 0);

  return products.map(toProduct);
};
